import React from 'react';
import {
  Activity,
  Car,
  Phone,
  ListTree,
  MessageSquare,
  FolderOpen,
  Image as ImageIcon,
} from 'lucide-react';

const formatHeroCreated = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '—';
  }
};

const KpiTile = ({ label, value, Icon }) => (
  <div
    className="min-w-[78px] rounded-lg px-3 py-2"
    style={{
      background: 'var(--cwp-card)',
      border: '1px solid var(--cwp-border)',
    }}
  >
    <div className="flex items-center gap-1.5 text-slate-500">
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <div className="mt-0.5 text-lg font-bold leading-tight text-slate-900">{value}</div>
  </div>
);

/**
 * @param {object} props
 * @param {object} props.caseData
 * @param {string} props.customerName
 * @param {() => void} props.onOpenCustomer
 * @param {{ services: number, notes: number, files: number, photos: number }} props.kpi
 */
const CaseWorkspaceHero = ({ caseData, customerName, onOpenCustomer, kpi }) => {
  const caseNum = caseData?.case?.case_number || '—';
  const created = formatHeroCreated(caseData?.case?.created_at);
  const phone = caseData?.customer?.mobile_number || '';
  const vehicleModel = caseData?.vehicle?.model || '';
  const plate = caseData?.vehicle?.registration_number || '';
  const initial = (customerName && customerName.charAt(0).toUpperCase()) || '?';

  return (
    <div className="cwp-surface-card-elevated relative mb-4 overflow-hidden p-5">
      <div
        className="pointer-events-none absolute -right-16 -top-24 h-80 w-80 rounded-full opacity-50"
        style={{
          background: 'radial-gradient(circle, oklch(0.62 0.18 262 / 0.2), transparent 70%)',
        }}
      />
      <div
        className="pointer-events-none absolute -bottom-16 -left-8 h-60 w-60 rounded-full opacity-40"
        style={{
          background: 'radial-gradient(circle, oklch(0.7 0.15 280 / 0.15), transparent 70%)',
        }}
      />
      <div className="relative flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
        <div className="flex min-w-0 items-center gap-4">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-lg font-bold text-white"
            style={{
              background: 'var(--cwp-gradient-primary)',
              boxShadow: 'var(--cwp-shadow-glow)',
            }}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                Case Workspace · RSA
              </div>
              <span className="h-1 w-1 shrink-0 rounded-full bg-slate-400/50" aria-hidden />
              <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                <Activity className="h-3 w-3 shrink-0" aria-hidden />
                Created {created}
              </div>
            </div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-2">
              <h1 className="font-mono text-[22px] font-bold leading-none tracking-tight text-slate-900 lg:text-2xl">
                {caseNum}
              </h1>
              <button
                type="button"
                onClick={onOpenCustomer}
                className="max-w-full truncate text-left text-[15px] font-semibold text-blue-600 hover:text-blue-800 hover:underline"
              >
                · {customerName}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-[12px] text-slate-500">
              {phone ? (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3 shrink-0" aria-hidden />
                  {phone}
                </span>
              ) : null}
              {vehicleModel ? (
                <span className="inline-flex items-center gap-1">
                  <Car className="h-3 w-3 shrink-0" aria-hidden />
                  {vehicleModel}
                </span>
              ) : null}
              {plate ? (
                <span className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-800">
                  {plate}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-4 gap-2">
          <KpiTile label="Services" value={kpi.services} Icon={ListTree} />
          <KpiTile label="Notes" value={kpi.notes} Icon={MessageSquare} />
          <KpiTile label="Files" value={kpi.files} Icon={FolderOpen} />
          <KpiTile label="Photos" value={kpi.photos} Icon={ImageIcon} />
        </div>
      </div>
    </div>
  );
};

export default CaseWorkspaceHero;
